/**
 * Demo fixtures shared by the navigation pages.
 *
 * Lifted from the old single-file page, where they sat as module-level
 * constants above 22 section components.
 */

import React from 'react';
import { Heart, UserRound } from 'lucide-react';
import editionsImg from '@ajat/design-system/assets/overall/side-menu/editions.jpg';
import patchImg from '@ajat/design-system/assets/overall/side-menu/patch-universe.png';
import qualityImg from '@ajat/design-system/assets/overall/side-menu/quality.jpg';
import crownCapImg from '@ajat/design-system/assets/crownstudent/side-menu/design-cap.webp';
import crownGlassesImg from '@ajat/design-system/assets/crownstudent/side-menu/champagne-glasses.webp';
import crownGearImg from '@ajat/design-system/assets/crownstudent/side-menu/student-gear.webp';
import crownSizeGuideImg from '@ajat/design-system/assets/crownstudent/side-menu/size-guide.webp';
import crownBlackBoxImg from '@ajat/design-system/assets/crownstudent/side-menu/black-box.webp';

// The demo links go nowhere — keep the dashboard URL
const stay = (e) => e.preventDefault();

/* SideMenu content (Figma OVER-LL 2388:6010) — the SideMenu page and the
   Overall Header demo (hamburger → SideMenu) share it */
export const DEMO_SIDE_MENU = {
  Overall: {
    title: 'Welcome',
    featured: { image: editionsImg, imageAlt: 'Overall with patches', label: 'DESIGNA EDITIONS', href: '#', onClick: stay },
    cards: [
      { image: patchImg, imageAlt: '', imageFit: 'contain', label: 'Patch Universe', href: '#', onClick: stay },
      { image: qualityImg, imageAlt: '', label: 'Quality', href: '#', onClick: stay },
    ],
    links: [
      { label: 'About Us', href: '#', onClick: stay },
      { label: 'Customer Service', href: '#', onClick: stay },
    ],
    actions: [
      { label: 'Mitt konto', icon: <UserRound size={16} strokeWidth={1.75} />, onClick: stay },
      { label: 'Mina designer', icon: <Heart size={16} strokeWidth={1.75} />, onClick: stay },
    ],
  },
};

export const DEMO_NAV_ITEMS = {
  CLS: [
    {
      label: 'STUDIETURTRØJE',
      href: '#',
      submenu: {
        watermark: 'Studieturtrøje',
        columns: [
          { type: 'card', cardType: 'button', image: require('../../../../assets/cls-dk/header/1.jpg'), imageAlt: 'Student trip hoodie', label: 'BESTIL STUDIETURTRØJER', href: '#' },
          { type: 'card', cardType: 'link', image: require('../../../../assets/cls-dk/header/2.jpg'), imageAlt: 'Create logotype', label: 'Create your logotype', href: '#' },
          { type: 'card', cardType: 'link', image: require('../../../../assets/cls-dk/header/3.jpg'), imageAlt: 'School shop', label: 'Open the School Shop', href: '#' },
          {
            type: 'links',
            items: [
              { label: 'Størrelses guide', href: '#' },
              { label: 'Tryk & Broderi', href: '#' },
              { label: 'Attraktive tøjpakker', href: '#' },
              { label: 'Om oss', href: '#' },
              { label: 'FAQ', href: '#' },
            ],
          },
        ],
      },
    },
    { label: 'STUDENTERUGENTRØJE', href: '#' },
    { label: 'STUDENTERHUE', href: '#' },
  ],
  ABC: [
    {
      label: 'Skolmerch',
      href: '#',
      submenu: {
        watermark: 'Skolmerch',
        columns: [
          { type: 'card', cardType: 'button', image: require('../../../../assets/abc-se/menu/menu-item-1.jpg'), imageAlt: 'Skolmerch collection', label: 'BUY SKOLMERCH', href: '#' },
          { type: 'card', cardType: 'link', image: require('../../../../assets/abc-se/menu/menu-item-2.jpg'), imageAlt: 'Size guide', label: 'Size Guide', href: '#' },
          {
            type: 'links',
            items: [
              { label: 'Customer Service', href: '#' },
              { label: 'Open School Shop', href: '#' },
              { label: 'About Us', href: '#' },
              { label: 'FAQ', href: '#' },
            ],
          },
        ],
      },
    },
    { label: 'Overalls & More', href: '#' },
    { label: 'Student Club', href: '#' },
    { label: 'Student Cap', href: '#' },
  ],
  // Figma Header/Clothing Brand=Overall (4150:4507) — plain links, no mega-menu yet
  Overall: [
    { label: 'OVERALLS', href: '#' },
    { label: 'PATCH UNIVERSE', href: '#' },
  ],
  // Figma Header/Clothing Brand=Crownstudent (4430:1697) — plain links
  Crownstudent: [
    { label: 'STUDENTMÖSSA', href: '#' },
    { label: 'CHAMPAGNEGLAS', href: '#' },
    { label: 'STUDENTPRYLAR', href: '#' },
  ],
};

export const DEMO_MOBILE_PROMO = {
  CLS: {
    show: true,
    image: require('../../../../assets/cls-dk/header/1.jpg'),
    imageAlt: 'Student trip hoodie promo',
    label: 'BESTIL STUDIETURTRØJER',
    href: '#',
  },
  ABC: {
    show: true,
    image: require('../../../../assets/abc-se/menu/menu-item-1.jpg'),
    imageAlt: 'Skolmerch promo',
    label: 'BUY SKOLMERCH',
    href: '#',
  },
};

// Crownstudent (Crown Student) — Crownstudent-MCP Navigation/SideMenu 52:16398:
// a 3:2 featured photo + four link cards (two rows)
DEMO_SIDE_MENU.Crownstudent = {
  title: 'Welcome',
  featured: { image: crownCapImg, imageAlt: 'Studentmössa', imageAspectRatio: '3 / 2', label: 'Börja Designa', href: '#', onClick: stay },
  cards: [
    { image: crownGlassesImg, imageAlt: '', label: 'Champagneglas', href: '#', onClick: stay },
    { image: crownGearImg, imageAlt: '', label: 'Studentprylar', href: '#', onClick: stay },
    { image: crownSizeGuideImg, imageAlt: '', label: 'Storleksguide', href: '#', onClick: stay },
    { image: crownBlackBoxImg, imageAlt: '', label: 'Black Box', href: '#', onClick: stay },
  ],
  links: [
    { label: 'Kundservice', href: '#', onClick: stay },
    { label: 'Om Crownstudent', href: '#', onClick: stay },
  ],
  actions: DEMO_SIDE_MENU.Overall.actions,
};
